import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Optional,
  computed,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {
  EntityListBlock,
  RichContentEntityType
} from '@core/services/ai/ai-assistant.service';
import { buildEntityDeeplink, defaultIconFor } from '../entity-deeplink.helper';
import { inAppRouteOrNull, normalizeQueryParams, safeInAppRoute } from '../safe-route.helper';

/** Inline rows shown before the rest collapses behind "Show N more". */
const DEFAULT_MAX_VISIBLE = 5;

@Component({
  selector: 'app-ai-entity-list-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, RouterLink],
  template: `
    @if (block.title) {
      <div class="elist__title">{{ block.title }}</div>
    }
    <ul class="elist">
      @for (item of visibleItems(); track item.id) {
        <li class="elist__row">
          @if (deeplinkFor(item)) {
            <a [routerLink]="deeplinkFor(item)" class="elist__link elist__link--clickable">
              <ng-container *ngTemplateOutlet="rowBody; context: {$implicit: item}"></ng-container>
            </a>
          } @else {
            <div class="elist__link">
              <ng-container *ngTemplateOutlet="rowBody; context: {$implicit: item}"></ng-container>
            </div>
          }
        </li>
      }
    </ul>
    @if (hiddenCount() > 0 || hasSeeAll()) {
      <div class="elist__footer">
        @if (hiddenCount() > 0) {
          <button type="button" class="elist__toggle" (click)="toggle()">
            {{ expanded() ? showLessText() : showMoreText() }}
          </button>
        }
        @if (hasSeeAll()) {
          <a
            class="elist__see-all"
            [routerLink]="safeSeeAllRoute()"
            [queryParams]="seeAllQueryParams()">
            {{ seeAllText() }}
            <mat-icon class="elist__see-all-arrow">arrow_forward</mat-icon>
          </a>
        }
      </div>
    }
    @if (block.caption) {
      <div class="elist__caption">{{ block.caption }}</div>
    }

    <ng-template #rowBody let-i>
      <mat-icon class="elist__icon">{{ i.icon || iconFor(i.entityType) }}</mat-icon>
      <div class="elist__main">
        <div class="elist__title-row">
          <span class="elist__title-text">{{ i.title }}</span>
          @if (i.badge) {
            <span class="elist__badge" [attr.data-tone]="i.badge.tone">{{ i.badge.label }}</span>
          }
        </div>
        @if (i.subtitle) {
          <div class="elist__sub">{{ i.subtitle }}</div>
        }
      </div>
      @if (i.amount) {
        <div class="elist__amount">{{ formatAmount(i.amount.value, i.amount.currency) }}</div>
      }
      @if (deeplinkFor(i)) {
        <span class="elist__open" aria-hidden="true">
          <mat-icon>chevron_right</mat-icon>
        </span>
      }
    </ng-template>
  `,
  styles: [`
    :host { display: block; }
    .elist__title {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: rgba(15, 23, 42, 0.55);
      margin: 0 0 6px;
    }
    .elist {
      list-style: none;
      margin: 0;
      padding: 0;
      background: #fff;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 12px;
      overflow: hidden;
    }
    .elist__row + .elist__row {
      border-top: 1px solid rgba(15, 23, 42, 0.06);
    }
    .elist__link {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      text-decoration: none;
      color: inherit;
    }
    .elist__link--clickable:hover {
      background: rgba(99, 102, 241, 0.06);
    }
    .elist__icon {
      flex-shrink: 0;
      font-size: 20px;
      width: 20px;
      height: 20px;
      color: #6366f1;
    }
    .elist__main { flex: 1; min-width: 0; }
    .elist__title-row {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    .elist__title-text {
      font-weight: 600;
      font-size: 14px;
      color: rgba(15, 23, 42, 0.92);
      overflow-wrap: anywhere;
      word-break: break-word;
      min-width: 0;
    }
    .elist__sub {
      font-size: 12px;
      color: rgba(15, 23, 42, 0.55);
      margin-top: 2px;
      overflow-wrap: anywhere;
    }
    .elist__amount {
      font-weight: 700;
      font-size: 14px;
      color: rgba(15, 23, 42, 0.92);
      flex-shrink: 0;
    }
    .elist__open {
      flex-shrink: 0;
      display: inline-flex;
      color: rgba(79, 70, 229, 0.85);
    }
    .elist__open mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
    .elist__badge {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      padding: 2px 6px;
      border-radius: 999px;
      background: rgba(99, 102, 241, 0.12);
      color: #4338ca;
    }
    .elist__badge[data-tone="positive"] { background: rgba(16, 185, 129, 0.12); color: #047857; }
    .elist__badge[data-tone="warning"]  { background: rgba(245, 158, 11, 0.12); color: #b45309; }
    .elist__badge[data-tone="danger"]   { background: rgba(239, 68, 68, 0.12); color: #b91c1c; }
    .elist__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
      margin-top: 6px;
    }
    .elist__toggle {
      border: none;
      background: none;
      padding: 2px 0;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      color: #4f46e5;
      cursor: pointer;
    }
    .elist__toggle:hover { text-decoration: underline; }
    .elist__see-all {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      margin-left: auto;
      font-size: 13px;
      font-weight: 600;
      color: #4f46e5;
      text-decoration: none;
    }
    .elist__see-all:hover { text-decoration: underline; }
    .elist__see-all-arrow {
      font-size: 15px;
      width: 15px;
      height: 15px;
    }
    .elist__caption {
      margin-top: 6px;
      font-size: 12px;
      color: rgba(15, 23, 42, 0.55);
      font-style: italic;
    }
  `]
})
export class AiEntityListBlockComponent {
  private readonly _block = signal<EntityListBlock | null>(null);
  readonly expanded = signal(false);

  /**
   * Constructor injection (over `inject()`) so the component can be
   * instantiated with `new` in pure unit tests without TestBed. A null
   * `translate` falls back to the English label.
   */
  constructor(@Optional() private readonly translate: TranslateService | null = null) {}

  @Input({ required: true }) set block(value: EntityListBlock) {
    this._block.set(value);
    // Reset the expander when a new block is bound so a re-used component
    // instance doesn't carry the previous list's expanded state.
    this.expanded.set(false);
  }
  get block(): EntityListBlock {
    return this._block()!;
  }

  private readonly maxVisible = computed<number>(() => {
    const n = this._block()?.maxVisible;
    return typeof n === 'number' && n > 0 ? n : DEFAULT_MAX_VISIBLE;
  });

  readonly visibleItems = computed<EntityListBlock['items']>(() => {
    const b = this._block();
    if (!b) return [];
    if (this.expanded()) return b.items;
    return b.items.slice(0, this.maxVisible());
  });

  readonly hiddenCount = computed<number>(() => {
    const b = this._block();
    if (!b) return 0;
    return Math.max(0, b.items.length - this.maxVisible());
  });

  readonly hasSeeAll = computed<boolean>(() => {
    const route = this._block()?.seeAllRoute;
    return typeof route === 'string' && route.length > 0;
  });

  readonly safeSeeAllRoute = computed(() => safeInAppRoute(this._block()?.seeAllRoute));

  readonly seeAllQueryParams = computed(() =>
    normalizeQueryParams(this._block()?.seeAllQueryParams),
  );

  readonly seeAllText = computed<string>(() => {
    const key = this._block()?.seeAllLabelKey ?? 'aiAssistant.list.seeAll';
    return this.resolveKey(key, 'See all');
  });

  readonly showMoreText = computed<string>(() => {
    const n = this.hiddenCount();
    const fallback = `Show ${n} more`;
    if (!this.translate) return fallback;
    const t = this.translate.instant('aiAssistant.list.showMore', { count: n });
    return typeof t === 'string' && t !== 'aiAssistant.list.showMore' ? t : fallback;
  });

  readonly showLessText = computed<string>(() =>
    this.resolveKey('aiAssistant.list.showLess', 'Show less'),
  );

  toggle(): void {
    this.expanded.update((v) => !v);
  }

  iconFor(entityType: RichContentEntityType): string {
    return defaultIconFor(entityType);
  }

  deeplinkFor(item: EntityListBlock['items'][number]): string | null {
    // An explicit route from the emitting tool wins — it knows the exact
    // collection (repairs vs maintenance, provider vs member). The
    // entityType-based guess remains the fallback for older payloads.
    return inAppRouteOrNull(item.route) ?? buildEntityDeeplink(item.entityType, item.id);
  }

  formatAmount(value: number, currency: string): string {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0
      }).format(value);
    } catch {
      return `${value} ${currency}`;
    }
  }

  private resolveKey(key: string | undefined, fallback: string): string {
    if (!key) return fallback;
    if (!this.translate) return fallback;
    const translated = this.translate.instant(key);
    if (typeof translated !== 'string' || translated === key) return fallback;
    return translated;
  }
}
